/* eslint-disable no-shadow */
/* eslint-disable react/require-default-props */
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { withErrorBoundary } from '@nile/components';
import { StatementCards } from '@nile/reports/src/modules/InvoiceStatements/StatementCards';
import { InvoiceStatementsChart } from '@nile/reports/src/modules/InvoiceStatements/InvoiceStatementsChart';
import InvoiceStatementsTable from '@nile/reports/src/modules/InvoiceStatements/InvoiceStatementsTable';
import { useInvoiceStatementsStore } from '@nile/reports/src/modules/InvoiceStatements/invoiceStatementsStore';
import ReportsContext from '@nile/reports/src/modules/home/contexts/Reports';
import { cloneDeep } from 'lodash';
import { StatementPreview } from '@nile/reports/src/modules/InvoiceStatements/StatementPreview';

function InvoiceStatementsSheet({ data }: { data: any }) {
  const reportParams = useParams();
  const {
    setClient,
    client: clientId,
    setClientName,
    reportAllowedDateRange
  } = useInvoiceStatementsStore();
  const reportsContext = useContext(ReportsContext) as {
    reportCondition?: Record<string, unknown[]>;
    activeView?: string;
    setReportCondition?: (val: Record<string, unknown[]>) => void;
  } | null;

  type ClientCondition = { attribute: string; value?: string; label?: string };

  const activeReportFilter: ClientCondition[] | undefined =
    reportsContext?.reportCondition && reportsContext?.activeView
      ? ((reportsContext.reportCondition[
          reportsContext.activeView
        ] as unknown) as ClientCondition[])
      : undefined;

  const allowedDateRange =
    activeReportFilter?.find((cond) => cond.attribute === 'createdTime') ??
    null;

  const handleClientClick = useCallback(
    (client: { accountId: string; name?: string }) => {
      if (!client?.accountId) return;
      try {
        const clientObj = {
          attribute: 'client',
          operator: 'is',
          value: client?.accountId,
          ...(client?.name && { label: client?.name })
        };

        if (activeReportFilter) {
          const updatedCondition = cloneDeep(
            (activeReportFilter || []) as ClientCondition[]
          );
          const existingIndex = updatedCondition.findIndex(
            (cond: ClientCondition) => cond.attribute === 'client'
          );
          if (existingIndex >= 0) {
            const prevValue = updatedCondition[existingIndex]?.value;
            updatedCondition[existingIndex] = clientObj as ClientCondition;
            if (prevValue !== clientObj?.value) {
              reportsContext?.setReportCondition?.({
                ...reportsContext?.reportCondition,
                [reportsContext?.activeView as string]: updatedCondition
              });
            }
          } else {
            reportsContext?.setReportCondition?.({
              ...reportsContext?.reportCondition,
              [reportsContext?.activeView as string]: [
                ...(updatedCondition as ClientCondition[]),
                clientObj
              ]
            });
          }
        } else {
          reportsContext?.setReportCondition?.({
            ...reportsContext?.reportCondition,
            [reportsContext?.activeView as string]: [clientObj]
          });
        }
        setClient(client.accountId ?? null);
        setClientName(client.name ?? '');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    [activeReportFilter, reportsContext, setClient, setClientName]
  );

  const resetReportClientFilter = useCallback(() => {
    const updatedCondition = cloneDeep(
      (activeReportFilter || []) as ClientCondition[]
    );
    const existingIndex = updatedCondition.findIndex(
      (cond: ClientCondition) => cond.attribute === 'client'
    );
    if (existingIndex >= 0) {
      updatedCondition.splice(existingIndex, 1);
      reportsContext?.setReportCondition?.({
        ...reportsContext?.reportCondition,
        [reportsContext?.activeView as string]: updatedCondition
      });
    }
    setClient(null);
  }, [activeReportFilter, reportsContext, setClient]);

  useEffect(() => {
    const clientObj = activeReportFilter?.find(
      (cond: ClientCondition) => cond?.attribute === 'client'
    );
    setClient(clientObj?.value ?? null);
    setClientName(clientObj?.label ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(activeReportFilter)]);

  // Expose handlers via zustand store for children if needed
  useEffect(() => {
    useInvoiceStatementsStore.setState({
      handleClientClick,
      resetReportClientFilter
    });
  }, [handleClientClick, resetReportClientFilter]);

  // Expose sheet props to store for children access
  useEffect(() => {
    let dateRange = null;
    if (allowedDateRange) {
      dateRange = { ...allowedDateRange, attribute: 'invoiceDate' };
    }
    useInvoiceStatementsStore.setState({
      reportData: data,
      reportAllowedDateRange: dateRange
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, JSON.stringify(allowedDateRange)]);

  const conditions = useMemo(
    () => (reportAllowedDateRange ? [reportAllowedDateRange] : []),
    [reportAllowedDateRange]
  );

  if (clientId) {
    return <StatementPreview />;
  }

  return (
    <div className="full-width full-height scroll">
      <div className="m20 rounded-4px bshadow">
        <StatementCards conditions={conditions} />
      </div>
      <div className="m20 p20 rounded-4px white-to-grey-bg bshadow">
        <InvoiceStatementsChart />
      </div>
      <div className="m10 rounded-4px white-to-grey-bg bshadow">
        <InvoiceStatementsTable
          reportId={reportParams?.id as string}
          onViewStatement={handleClientClick}
        />
      </div>
    </div>
  );
}

export default withErrorBoundary(InvoiceStatementsSheet);
